"use client"

import { useState } from "react"
import { useRouter } from "next/navigation"
import { Card, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { BookOpen, Users, ArrowRight, Sparkles } from "lucide-react"
import { R2Image } from "@/components/r2-image"

interface CollectionCardProps {
  collection: {
    id: string
    title: string
    description: string
    tagline?: string
    category: string
    isFeatured: boolean
    visitCount: number
    books: Array<{
      book: {
        id: string
        title: string
        author: string
        coverUrl?: string
      }
    }>
    _count: {
      books: number
    }
  }
}

export function CollectionCard({ collection }: CollectionCardProps) {
  const router = useRouter()
  const [isHovered, setIsHovered] = useState(false)

  const handleClick = () => {
    router.push(`/collections/${collection.id}`)
  }

  // theme styling removed

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'challenge': return <Sparkles className="h-3 w-3 sm:h-4 sm:w-4" />
      case 'inspiration': return <Users className="h-3 w-3 sm:h-4 sm:w-4" />
      default: return <BookOpen className="h-3 w-3 sm:h-4 sm:w-4" />
    }
  }

  return (
    <Card 
      className={`group cursor-pointer transition-all duration-300 sm:duration-500 hover:shadow-xl sm:hover:shadow-2xl hover:shadow-purple-500/20 dark:hover:shadow-purple-500/40 hover:scale-[1.02] sm:hover:scale-[1.05] hover:-translate-y-1 sm:hover:-translate-y-2 bg-gradient-to-br from-white via-white to-purple-50/30 dark:from-slate-900 dark:via-slate-900 dark:to-purple-950/30 border backdrop-blur-sm h-full flex flex-col ${
        collection.isFeatured ? 'border-purple-200 dark:border-purple-800' : 'border-slate-200 dark:border-slate-800'
      }`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handleClick}
    >
      <CardContent className="p-3 sm:p-6 relative overflow-hidden flex-1 flex flex-col">
        {/* Subtle gradient overlay on hover */}
        <div className="absolute inset-0 bg-gradient-to-br from-purple-500/0 via-pink-500/0 to-violet-500/0 group-hover:from-purple-500/5 group-hover:via-pink-500/5 group-hover:to-violet-500/5 transition-all duration-500"></div>
        
        {/* Content */}
        <div className="relative z-10 flex-1 flex flex-col">
        {/* Header */}
        <div className="flex items-start justify-between mb-2 sm:mb-4 flex-shrink-0">
          <div className="flex-1">
            <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mb-2 sm:mb-3">
              {collection.isFeatured && (
                <Badge className="text-[10px] sm:text-xs px-1.5 sm:px-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0 shadow-md">
                  <Sparkles className="h-2.5 w-2.5 sm:h-3 sm:w-3 mr-1" />
                  Featured
                </Badge>
              )}
              <Badge 
                variant="outline"
                className="text-[10px] sm:text-xs px-1.5 sm:px-2 bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm"
              >
                {getCategoryIcon(collection.category)}
                <span className="ml-1 capitalize">{collection.category}</span>
              </Badge>
            </div>
            <h3 className="text-base sm:text-xl font-bold text-foreground line-clamp-2 group-hover:bg-gradient-to-r group-hover:from-purple-600 group-hover:to-pink-600 group-hover:bg-clip-text group-hover:text-transparent transition-all duration-300">
              {collection.title}
            </h3>
            {collection.tagline && (
              <p className="text-xs sm:text-sm text-muted-foreground mt-1.5 sm:mt-2 italic line-clamp-2">
                {collection.tagline}
              </p>
            )}
          </div>
        </div>

        {/* Description */}
        <p className="text-muted-foreground text-xs sm:text-sm mb-2 sm:mb-4 line-clamp-2 sm:line-clamp-3 flex-shrink-0">
          {collection.description}
        </p>

        {/* Book Count and Preview */}
        <div className="flex items-center justify-between mb-2 sm:mb-4 flex-shrink-0 mt-auto">
          <div className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm text-muted-foreground">
            <BookOpen className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
            <span>{collection._count.books} books</span>
          </div>
          
          {/* Book covers preview */}
          <div className="flex -space-x-2">
            {collection.books.slice(0, 3).map((item, index) => (
              <div
                key={item.book.id}
                className="w-6 h-6 sm:w-8 sm:h-8 rounded-full border-2 border-background bg-muted flex items-center justify-center text-[10px] sm:text-xs font-medium overflow-hidden"
                style={{ zIndex: 3 - index }}
              >
                {item.book.coverUrl ? (
                  <R2Image
                    src={item.book.coverUrl}
                    alt={item.book.title}
                    width={24}
                    height={24}
                    className="w-full h-full rounded-full object-cover object-center"
                    fallbackIcon={<BookOpen className="h-4 w-4 text-muted-foreground" />}
                  />
                ) : (
                  <span className="text-muted-foreground">
                    {item.book.title.charAt(0)}
                  </span>
                )}
              </div>
            ))}
            {collection._count.books > 3 && (
              <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-full border-2 border-background bg-muted flex items-center justify-center text-[10px] sm:text-xs font-medium">
                +{collection._count.books - 3}
              </div>
            )}
          </div>
        </div>

        {/* Action Button */}
        <Button 
          variant="outline" 
          className="w-full h-8 sm:h-10 px-2 sm:px-3 text-[11px] sm:text-sm leading-none bg-gradient-to-r from-purple-600/10 to-pink-600/10 hover:from-purple-600 hover:to-pink-600 hover:text-white border-purple-200 dark:border-purple-800 group-hover:border-transparent transition-all duration-300 shadow-sm hover:shadow-lg flex-shrink-0 mt-2 sm:mt-4"
        >
          <span className="font-semibold whitespace-nowrap sm:hidden">Explore</span>
          <span className="font-semibold whitespace-nowrap hidden sm:inline">Explore Collection</span>
          <ArrowRight className={`h-3.5 w-3.5 sm:h-4 sm:w-4 ml-1 sm:ml-2 shrink-0 transition-transform duration-300 ${
            isHovered ? 'translate-x-2' : ''
          }`} />
        </Button>
        </div>
      </CardContent>
    </Card>
  )
}
